import { create } from 'zustand';
import { OfflineError, api } from './client';

export type Role = 'admin' | 'instructor';
export type User = { id: string; username: string; role: Role; displayName: string };

// 'offline' means the server could not be reached at all, distinct from 'anonymous' (reached,
// no valid session). The UI offers a local, server-free run only in the 'offline' case.
export type SessionState = 'loading' | 'anonymous' | 'ready' | 'offline';

type Session = {
  user: User | null;
  state: SessionState;
  refresh: () => Promise<void>;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
};

export const useSession = create<Session>((set) => ({
  user: null,
  state: 'loading',

  async refresh() {
    try {
      const user = await api.get<User>('/api/auth/me');
      set({ user, state: 'ready' });
    } catch (e) {
      set({ user: null, state: e instanceof OfflineError ? 'offline' : 'anonymous' });
    }
  },

  async login(username, password) {
    try {
      const user = await api.post<User>('/api/auth/login', { username, password });
      set({ user, state: 'ready' });
    } catch (e) {
      // Unreachable server: switch to the offline view, which offers the file import.
      if (e instanceof OfflineError) set({ user: null, state: 'offline' });
      throw e;
    }
  },

  async logout() {
    // Best effort: even if the server cannot be reached, the local session still clears.
    await api.post('/api/auth/logout').catch(() => undefined);
    set({ user: null, state: 'anonymous' });
  },
}));
